<template>
  <div>
    <div
      class="dropzone dz-clickable d-flex align-items-center px-3 py-3 mb-2"
      @dragover.prevent="dragOver"
      @dragleave.prevent="dragLeave"
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
      :class="{
        dragging: isDragging,
        'bg-danger-subtle': hasErrors,
        'bg-success-subtle': hasUploadedFile,
        'text-success-emphasis': hasUploadedFile,
      }"
    >
      <input
        class="form-control visually-hidden"
        type="file"
        @change="handleFileUpload"
        ref="fileInput"
      />
      <div class="dropzone-content w-100">
        <div class="d-flex align-items-center">
          <div class="upload-icon flex-shrink-0">
            <font-awesome-icon v-if="isLoading" icon="spinner" spin />
            <font-awesome-icon
              v-else
              :icon="hasUploadedFile ? 'check' : 'cloud-upload'"
            />
          </div>
          <div class="text-start flex-grow-1">
            <p class="mb-1">
              {{ uploadTitle }}
              <span v-if="displayAsterisk" class="text-danger">*</span>
            </p>
            <small>{{ supportedFormats }}</small>
          </div>
        </div>
      </div>
    </div>
    <div
      class="invalid-feedback mb-2 d-flex justify-content-center align-items-center"
      v-if="wrongFileError"
    >
      <div>
        <strong>{{ uploadErrorMessage }}</strong>
        <div>{{ $t("common.invalid_file_contact_support") }}</div>
      </div>
    </div>
    <div
      class="invalid-feedback mb-2 d-flex justify-content-center align-items-center"
      v-for="error of v$.hasUploadedFile.$errors"
      :key="error.$uid"
    >
      <strong>{{ error.$message }}</strong>
    </div>
  </div>
</template>

<script setup lang="ts">
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useValidators } from "@/composables/useValidators";
import { useVuelidate } from "@vuelidate/core";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

type QueryParams = Record<string, string>;

const props = withDefaults(
  defineProps<{
    uploadTitle: string;
    supportedFormats: string;
    uploadUrl: string;
    queryParams: QueryParams;
    fileUrl?: string;
    uploadErrorMessage: string;
    required?: boolean;
    displayAsterisk?: boolean;
  }>(),
  {
    fileUrl: "",
    required: true,
    displayAsterisk: false,
  }
);

const emit = defineEmits<{
  uploadComplete: [data: any];
  errorOccurred: [error: any];
  uploadStart: [];
}>();

const isDragging = ref(false);
const isLoading = ref(false);
const wrongFileError = ref(false);
const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const { globalErrorHandler } = useGlobalHandlers();

const hasUploadedFile = computed(() => props.fileUrl !== "");

const processFile = async (file: File) => {
  try {
    isLoading.value = true;
    wrongFileError.value = false;
    emit("uploadStart");
    const formData = new FormData();
    formData.append("file", file);
    // Add each field from metadata into the formData
    for (const [key, value] of Object.entries(props.queryParams)) {
      formData.append(key, value);
    }

    const response = await goSolidApi.post(props.uploadUrl, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (response.data) {
      emit("uploadComplete", response.data);
    } else {
      throw new Error("Error uploading the document");
    }
  } catch (error: any) {
    if (
      error.response &&
      error.response.data.message === "Invalid document provided."
    ) {
      wrongFileError.value = true;
    } else {
      globalErrorHandler(error);
    }
    // Emit error events for backward compatibility and correct spelling
    emit("errorOccurred", error);
  } finally {
    isLoading.value = false;
  }
};

const handleFileUpload = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) processFile(file);
};

const dragOver = () => {
  isDragging.value = true;
};

const dragLeave = () => {
  isDragging.value = false;
};

const handleDrop = (event: DragEvent) => {
  const file = event.dataTransfer?.files[0];
  if (file) processFile(file);
  isDragging.value = false;
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

const { t } = useI18n();
const { fileRequired } = useValidators(t);

const rules = computed(() => {
  return props.required ? { fileRequired } : {};
});

const v$ = useVuelidate({ hasUploadedFile: rules }, { hasUploadedFile });

const hasErrors = computed(() => v$.value.hasUploadedFile.$error);
</script>

<style scoped lang="scss">
.dropzone {
  border: 2px dashed var(--phoenix-secondary-text-emphasis);
  border-radius: 10px;
  min-height: 90px;
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  color: var(--phoenix-text-color);
  transition-duration: 200ms;
  transition-property: background-color;
  transition-timing-function: ease-out;

  &.bg-success-subtle {
    border: 1px solid var(--phoenix-success-text-emphasis);
  }

  &.dragging {
    background-color: #e9ecef;
  }

  &:hover {
    background-color: var(--phoenix-primary-bg-subtle);
    color: var(--phoenix-primary-text-emphasis);
    transition-duration: 200ms;
    transition-property: background-color;
    transition-timing-function: ease-out;
  }

  .dropzone-content {
    text-align: left;

    .upload-icon {
      font-size: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-right: 1rem;
      width: 3rem;
    }

    p {
      font-weight: 600;
      font-size: 1rem;
      margin: 0;
      line-height: 1.3;
    }

    small {
      display: block;
      font-size: 0.85rem;
      margin-top: 2px;
      opacity: 0.9;
    }
  }
}
</style>
